// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// VIEW 3 — "State by state".
//
// Eight area-true small multiples. Deliberately NOT a second map (the map view
// already does geography) and deliberately NOT eight equal bars (equal bars
// would say Victoria and Western Australia are comparable containers, which is
// the single most misleading thing this page could say).
//
// THE ENCODING, in one place:
//
//   * Each square's AREA is proportional to the jurisdiction's real land area,
//     so side ∝ √landKm². Western Australia's square is a thousand times the
//     Australian Capital Territory's because the ground is.
//   * Each square FILLS FROM THE BOTTOM. Because the fill is full-width, the
//     filled height fraction IS the filled area fraction, so the painted area
//     of a square is proportional to real square kilometres across every square
//     on the page. Exclusive possession sits at the base (solid), non-exclusive
//     above it (stippled) — a tall fill and a tall base are visibly different
//     claims, which is the whole point: South Australia is 51.0% recognised and
//     0.8% exclusive.
//   * "Does not exist" and "extinguished" are a DETACHED sub-bar beside the
//     square, on the same vertical scale but never inside the stack. The four
//     categories are four separately measured unions; they do not partition a
//     jurisdiction, and stacking them would assert that they do.
//   * Method is not encoded here at all. Method is a stroke, and a stroke on a
//     4-pixel block is a lie.
//
// THE FLOOR IS DECLARED, NEVER SILENT. At true scale the ACT's square is about
// eight pixels across — invisible and unclickable. Every square therefore has a
// minimum rendered size, the tiles that hit it say so in their own caption and
// in their tooltip (with the true-scale size), and the caption under the grid
// names them. A distorted scale that is stated is a scale; one that is not is a
// defect.
//
// TASMANIA AND THE ACT ARE NOT MISSING DATA. They are complete, present,
// labelled, clickable squares with an explicit "no determination has ever been
// made here". Greying them out would say the registers are silent about them;
// the registers are not silent, they record zero.

import { TENURE, TENURE_ORDER, ALRA_CAVEAT, type Tenure } from '../theme';
import { el, chart, measure, defs, tenureFill } from '../components/svg';
import { esc, num, km2, pct, tip, tipAttr, plural } from '../format';
import { glossTerm, glossIcon } from '../components/glossary';
import { openOverlay, closeButton } from '../overlay';
import { navigate } from '../router';
import { getMeta, loadJurisdictions } from '../data';
import type { Jurisdiction, Meta } from '../types';

/** The smallest square we will draw. Below this a tile is neither visible nor a touch target. */
const FLOOR_PX = 46;
/** A non-zero area smaller than a pixel is still drawn, at one pixel, and the caption says so. */
const MIN_MARK = 1;
const SUB_W = 14;
const SUB_GAP = 10;
/** Enough width for a jurisdiction name to wrap sanely under a tiny square. */
const MIN_CELL = 140;

const TERRITORIES = new Set(['NT', 'ACT']);
const noun = (code: string) => (TERRITORIES.has(code) ? 'Territory' : 'State');

const shareOf = (v: number, land: number) => (land > 0 ? (v / land) * 100 : 0);

/** The national union for a category, from meta — used only in the legend. */
function nationalArea(meta: Meta, t: Tenure): number {
  switch (t) {
    case 'exclusive': return meta.areas.exclusiveKm2;
    case 'nonexclusive': return meta.areas.nonExclusiveKm2;
    case 'notexist': return meta.areas.notExistKm2;
    case 'extinguished': return meta.areas.extinguishedKm2;
  }
}

/** A legend swatch that carries the same texture as the mark it stands for. */
function swatchStyle(t: Tenure): string {
  const s = TENURE[t];
  const parts = [`background:${s.fill}`];
  if (s.texture === 'stipple') {
    parts.push('background-image:radial-gradient(rgba(255,255,255,.75) 1px, transparent 1px)');
    parts.push('background-size:4px 4px');
  }
  if (s.texture === 'hatch') {
    parts.push('background-image:repeating-linear-gradient(45deg, rgba(255,255,255,.7) 0 2px, transparent 2px 4px)');
  }
  return parts.join(';');
}

/* ───────────────────────────── one small multiple ───────────────────────── */

function buildTile(j: Jurisdiction, side: number, trueSide: number, boxH: number): HTMLButtonElement {
  const floored = side > trueSide + 0.5;
  const land = j.landKm2;
  const inner = side - 2;
  // Every square sits on ONE ground line, in every row, whatever its size: the
  // drawing box is as tall as the largest square and the square is drawn at the
  // bottom of it. Without this the fill levels of two squares in the same row
  // float at different heights and cannot be read against each other.
  const y0 = boxH - side;
  const base = boxH - 1;
  const n = noun(j.code);
  const nothing = j.determinations === 0;

  // Height for an area, on the shared scale: the full square height is the
  // whole jurisdiction. A non-zero area below a pixel is pinned to one pixel so
  // it is present rather than invisible; the caption declares that.
  const hOf = (v: number) => (v > 0 ? Math.max(MIN_MARK, (v / land) * inner) : 0);

  const exPct = shareOf(j.exclusiveKm2, land);
  const nonPct = shareOf(j.nonExclusiveKm2, land);
  const notPct = shareOf(j.notExistKm2, land);
  const extPct = shareOf(j.extinguishedKm2, land);
  const exOfRec = j.recognisedKm2 > 0 ? (j.exclusiveKm2 / j.recognisedKm2) * 100 : 0;

  const svgW = side + SUB_GAP + SUB_W;
  const svg = chart(svgW, boxH, `${j.name}, drawn at its share of the continent`);
  // The button below carries the whole accessible description; the marks inside
  // would otherwise be announced twice and add nothing.
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('jt-art');
  defs(svg);

  /* ── the square: a transparent hit area first, so the blocks drawn on top of
        it keep their own tooltips and the bare ground keeps the summary. ── */
  const hit = el('rect', {
    x: 1, y: y0 + 1, width: inner, height: inner, fill: 'transparent', 'pointer-events': 'all',
  });
  hit.setAttribute('data-tip', tip(
    `${j.name} (${j.code})`,
    `Land area ${km2(land, { exact: true })} — Geoscience Australia, land only`,
    nothing
      ? 'No determination has ever been made here — 0 km² recognised, 0.0% of the Territory or State.'
      : `Native title recognised ${km2(j.recognisedKm2, { exact: true })} — ${pct(j.recognisedPct)} of the ${n}`,
    nothing ? null : `Exclusive possession ${km2(j.exclusiveKm2, { exact: true })} — ${pct(exPct)} of the ${n}, ${pct(exOfRec)} of what is recognised here`,
    `${plural(j.determinations, 'determination')} · ${plural(j.agreements, 'agreement')} · ${plural(j.liveMatters, 'live matter')}`,
    'Dissolved union, land only.',
    floored ? `Square drawn at the ${FLOOR_PX} px minimum — at true scale it would be about ${Math.max(1, Math.round(trueSide))} px across.` : null,
    'Click to see this jurisdiction on the map.',
  ));
  svg.append(hit);

  const hEx = hOf(j.exclusiveKm2);
  const hNon = hOf(j.nonExclusiveKm2);

  if (hEx > 0) {
    const r = el('rect', {
      x: 1, y: base - hEx, width: inner, height: hEx, fill: tenureFill('exclusive'), class: 'mark',
    });
    r.setAttribute('data-tip', tip(
      TENURE.exclusive.register,
      `${km2(j.exclusiveKm2, { exact: true })} — ${pct(exPct)} of ${j.name}’s land area`,
      `${pct(exOfRec)} of the area recognised in this ${n.toLowerCase()}`,
      j.exclusiveKm2 > 0 && hEx <= MIN_MARK ? 'Smaller than one pixel at this scale — drawn one pixel high so it is present.' : null,
      'Dissolved union, land only.',
    ));
    svg.append(r);
  }
  if (hNon > 0) {
    const r = el('rect', {
      x: 1, y: base - hEx - hNon, width: inner, height: hNon, fill: tenureFill('nonexclusive'), class: 'mark',
    });
    r.setAttribute('data-tip', tip(
      TENURE.nonexclusive.register,
      `${km2(j.nonExclusiveKm2, { exact: true })} — ${pct(nonPct)} of ${j.name}’s land area`,
      `${pct(100 - exOfRec)} of the area recognised in this ${n.toLowerCase()}`,
      j.nonExclusiveKm2 > 0 && hNon <= MIN_MARK ? 'Smaller than one pixel at this scale — drawn one pixel high so it is present.' : null,
      'Dissolved union, land only.',
    ));
    svg.append(r);
  }

  // The outline last, over the fills, with fill:none so it never takes a hover.
  svg.append(el('rect', {
    x: 0.5, y: y0 + 0.5, width: side - 1, height: side - 1,
    fill: 'none', stroke: 'var(--border-strong)', 'stroke-width': 1, class: 'jt-outline',
  }));

  if (nothing) {
    // A real, labelled zero inside a complete square — never a grey "no data" box.
    svg.append(el('text', {
      x: side / 2, y: y0 + side / 2 + 4, 'text-anchor': 'middle', class: 'jt-zero-mark',
    }, '0'));
  }

  /* ── the DETACHED sub-bar: same vertical scale, explicitly not the same stack ── */
  const sx = side + SUB_GAP;
  const subHit = el('rect', {
    x: sx, y: y0 + 1, width: SUB_W, height: inner, fill: 'transparent', 'pointer-events': 'all',
  });
  subHit.setAttribute('data-tip', tip(
    'Beside the square, not inside it',
    `${TENURE.notexist.register}: ${km2(j.notExistKm2, { exact: true })} — ${pct(notPct)} of ${j.name}`,
    `${TENURE.extinguished.register}: ${km2(j.extinguishedKm2, { exact: true })} — ${pct(extPct)} of ${j.name}`,
    'The four categories do not partition the jurisdiction — most ground is in none of them, because no determination has considered it.',
    'Same vertical scale as the square: the full height is the whole jurisdiction. Dissolved union, land only.',
  ));
  svg.append(subHit);

  const hNot = hOf(j.notExistKm2);
  const hExt = hOf(j.extinguishedKm2);
  if (hNot > 0) {
    const r = el('rect', { x: sx, y: base - hNot, width: SUB_W, height: hNot, fill: tenureFill('notexist'), class: 'mark' });
    r.setAttribute('data-tip', tip(
      TENURE.notexist.register,
      `${km2(j.notExistKm2, { exact: true })} — ${pct(notPct)} of ${j.name}’s land area`,
      TENURE.notexist.plain,
      j.notExistKm2 > 0 && hNot <= MIN_MARK ? 'Smaller than one pixel at this scale — drawn one pixel high so it is present.' : null,
      'Dissolved union, land only.',
    ));
    svg.append(r);
  }
  if (hExt > 0) {
    const r = el('rect', { x: sx, y: base - hNot - hExt, width: SUB_W, height: hExt, fill: tenureFill('extinguished'), class: 'mark' });
    r.setAttribute('data-tip', tip(
      TENURE.extinguished.register,
      `${km2(j.extinguishedKm2, { exact: true })} — ${pct(extPct)} of ${j.name}’s land area`,
      TENURE.extinguished.plain,
      j.extinguishedKm2 > 0 && hExt <= MIN_MARK ? 'Smaller than one pixel at this scale — drawn one pixel high so it is present.' : null,
      'Dissolved union, land only.',
    ));
    svg.append(r);
  }
  if (hNot === 0 && hExt === 0) {
    // Present, labelled zero — a baseline tick, not an absence.
    svg.append(el('line', {
      x1: sx, y1: base, x2: sx + SUB_W, y2: base, stroke: 'var(--text-secondary)', 'stroke-width': 1.6,
    }));
    svg.append(el('text', { x: sx + SUB_W / 2, y: base - 5, 'text-anchor': 'middle', class: 'jt-zero-mark small-zero' }, '0'));
  }
  // The track shows what the sub-bar is measured against. Drawn last, fill:none.
  svg.append(el('rect', {
    x: sx - 0.5, y: y0 + 0.5, width: SUB_W + 1, height: side - 1,
    fill: 'none', stroke: 'var(--border-subtle)', 'stroke-width': 1,
  }));

  /* ── the tile ── */
  const tile = document.createElement('button');
  tile.type = 'button';
  tile.className = 'jt-tile';
  tile.style.width = `min(${Math.max(svgW, MIN_CELL)}px, 100%)`;
  tile.setAttribute('aria-label', nothing
    ? `${j.name}: no determination has ever been made here. Land area ${km2(land, { exact: true })}. Show ${j.name} on the map.`
    : `${j.name}: native title recognised over ${pct(j.recognisedPct)} of the ${n}’s land area, of which ${pct(exOfRec)} is exclusive possession. ${plural(j.determinations, 'determination')}. Show ${j.name} on the map.`);
  tile.addEventListener('click', () => navigate('map', { juris: j.code }));

  const cap = document.createElement('div');
  cap.className = 'jt-cap';
  cap.innerHTML = `
    <div class="jt-name">${esc(j.name)} <span class="jt-code">${esc(j.code)}</span></div>
    ${nothing
      ? `<div class="jt-zero">No determination has ever been made here.</div>
         <div class="jt-num muted">A real zero, not missing data.</div>`
      : `<div class="jt-num"><span class="mono">${esc(pct(j.recognisedPct))}</span> of the ${esc(n)}’s land recognised</div>
         <div class="jt-num muted">${j.exclusiveKm2 > 0
            ? `<span class="mono">${esc(pct(exPct))}</span> exclusive possession`
            : 'No exclusive possession recognised'}</div>`}
    <div class="jt-meta xsmall muted">${esc(num(j.determinations))} determinations · ${esc(num(j.agreements))} agreements · ${esc(num(j.liveMatters))} live matters</div>
    ${floored ? `<div class="jt-floor xsmall">Drawn at the ${FLOOR_PX}&nbsp;px minimum size (true scale ≈ ${esc(String(Math.max(1, Math.round(trueSide))))}&nbsp;px).</div>` : ''}
    ${j.code === 'NT' ? '<div class="jt-floor xsmall">Read the note beside this square before reading this number.</div>' : ''}
  `;

  tile.append(svg, cap);
  return tile;
}

/* ─────────────────────────────── the ALRA note ───────────────────────────── */

function openAlra(nt: Jurisdiction): void {
  openOverlay({
    panelClass: 'modal',
    label: 'The Aboriginal Land Rights (Northern Territory) Act 1976',
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = 'Why the Northern Territory’s figure is not a measure of Aboriginal land';
      head.append(h, closeButton(close, 'Close this note'));

      const body = document.createElement('div');
      body.className = 'stack';
      body.innerHTML = `
        <p>${esc(ALRA_CAVEAT)}</p>
        <div class="note">
          On these registers the Northern Territory shows
          <strong>${esc(km2(nt.recognisedKm2, { exact: true }))}</strong> of native title recognised —
          <strong>${esc(pct(nt.recognisedPct))}</strong> of the Territory’s ${esc(km2(nt.landKm2, { exact: true }))} of land,
          of which ${esc(km2(nt.exclusiveKm2, { exact: true }))} is exclusive possession.
          That figure counts native title and nothing else.
        </div>
        <p class="small muted">Land held as Aboriginal freehold under the 1976 Act is administered through Land Councils
          and land trusts, not through the National Native Title Register, so it appears nowhere in the seven registers
          behind this site. The two systems can also overlap in the same place: a native title determination can be made
          over land that is already Aboriginal freehold.</p>
        <p class="small">${glossTerm('alra', 'What the Aboriginal Land Rights (NT) Act is')} ·
          ${glossTerm('native-title', 'What native title is')} ·
          ${glossTerm('determination', 'What a determination is')}</p>
      `;
      panel.append(head, body);
    },
  });
}

function alraCard(nt: Jurisdiction): HTMLElement {
  const card = document.createElement('aside');
  card.className = 'jt-alra';
  card.innerHTML = `
    <div class="jt-alra-head">
      <span class="jt-alra-num mono">${esc(pct(nt.recognisedPct))}</span>
      <span class="jt-alra-label">Northern Territory, on this register${glossIcon('alra')}</span>
    </div>
    <p class="small">${esc(ALRA_CAVEAT)}</p>
    <button type="button" class="btn btn-sm jt-alra-more">Read the full note</button>
  `;
  card.querySelector<HTMLButtonElement>('.jt-alra-more')!.addEventListener('click', () => openAlra(nt));
  return card;
}

/* ──────────────────────────────── the view ───────────────────────────────── */

export async function renderJurisdictions(host: HTMLElement): Promise<void> {
  const meta = getMeta();
  const rows = await loadJurisdictions();
  // Ordering jurisdictions by land area is administrative fact, and it is also
  // the order that packs an area-true grid without leaving holes.
  const juris = [...rows].sort((a, b) => b.landKm2 - a.landKm2);

  const nt = juris.find((j) => j.code === 'NT') ?? null;
  const wa = juris.find((j) => j.code === 'WA') ?? null;
  const sa = juris.find((j) => j.code === 'SA') ?? null;
  const nsw = juris.find((j) => j.code === 'NSW') ?? null;
  const emptyOnes = juris.filter((j) => j.determinations === 0);

  const waExclusiveShare = wa && meta.areas.exclusiveKm2 > 0
    ? (wa.exclusiveKm2 / meta.areas.exclusiveKm2) * 100 : 0;

  const w = measure(host, 900);
  const maxSide = w < 560 ? Math.min(206, Math.max(120, w - 56)) : Math.min(320, Math.round(w * 0.30));
  const maxLand = Math.max(...juris.map((j) => j.landKm2));
  const scale = maxSide / Math.sqrt(maxLand);
  const trueSideOf = (j: Jurisdiction) => Math.sqrt(j.landKm2) * scale;
  const sideOf = (j: Jurisdiction) => Math.max(FLOOR_PX, Math.round(trueSideOf(j)));
  const flooredNames = juris.filter((j) => sideOf(j) > trueSideOf(j) + 0.5).map((j) => j.name);

  /* ── head ── */
  const head = document.createElement('div');
  head.className = 'view-head';
  head.innerHTML = `
    <h1>State by state</h1>
    <p class="lede">Why does this look so different in Western Australia and the Northern Territory —
      and why is Tasmania blank?</p>
    <p>${glossTerm('native-title', 'Native title')} has been recognised over
      ${esc(km2(meta.areas.recognisedKm2))} of Australia, ${esc(pct(meta.areas.recognisedPct))} of the continent’s land.
      Almost none of that is spread evenly.
      ${wa ? `Western Australia holds ${esc(pct(waExclusiveShare))} of all the
        ${glossTerm('exclusive-possession', 'exclusive possession')} recognised in the country.` : ''}
      ${sa ? `South Australia has ${esc(pct(sa.recognisedPct))} of the State recognised and
        ${esc(pct(shareOf(sa.exclusiveKm2, sa.landKm2)))} of it exclusive.` : ''}
      ${nsw ? `New South Wales has ${esc(num(nsw.determinations))} ${glossTerm('determination', 'determinations')} and
        ${esc(pct(nsw.recognisedPct))} of the State recognised.` : ''}
      ${emptyOnes.length ? `${esc(emptyOnes.map((j) => j.name).join(' and '))} have never had a determination at all.` : ''}
      ${nt ? `And the Northern Territory’s ${esc(pct(nt.recognisedPct))} is the one number on this page that cannot be
        read at face value — the note beside its square explains why.` : ''}</p>
  `;

  /* ── the four headline figures ── */
  const stats = document.createElement('div');
  stats.className = 'stat-row';
  stats.innerHTML = `
    <div class="stat" data-tip="${tipAttr(
      `${km2(meta.areas.recognisedKm2, { exact: true })} of ${km2(meta.australiaLandKm2, { exact: true })}`,
      'Dissolved union in an equal-area projection, land only.',
    )}">
      <div class="stat-value">${esc(pct(meta.areas.recognisedPct))}</div>
      <div class="stat-label">Recognised across Australia</div>
      <div class="stat-note">${esc(km2(meta.areas.recognisedKm2, { exact: true }))} of ${esc(km2(meta.australiaLandKm2, { exact: true }))}</div>
    </div>
    <div class="stat" data-tip="${tipAttr(
      wa ? `Western Australia ${km2(wa.exclusiveKm2, { exact: true })}` : '',
      `All jurisdictions ${km2(meta.areas.exclusiveKm2, { exact: true })}`,
      'Exclusive possession is generally only available over ground where no other interest was ever created.',
    )}">
      <div class="stat-value">${esc(pct(waExclusiveShare))}</div>
      <div class="stat-label">Of all exclusive possession is in Western Australia</div>
      <div class="stat-note">${wa ? esc(km2(wa.exclusiveKm2)) : '—'} of ${esc(km2(meta.areas.exclusiveKm2))}</div>
    </div>
    <div class="stat" data-tip="${tipAttr(
      emptyOnes.map((j) => `${j.name}: 0 determinations, 0 agreements, 0 live matters`).join('\n'),
      'A recorded zero, not missing data.',
    )}">
      <div class="stat-value">${esc(num(emptyOnes.length))}</div>
      <div class="stat-label">Jurisdictions with no determination, ever</div>
      <div class="stat-note">${esc(emptyOnes.map((j) => j.name).join(' · ')) || '—'}</div>
    </div>
    <div class="stat" data-tip="${tipAttr(
      nt ? `${km2(nt.recognisedKm2, { exact: true })} of ${km2(nt.landKm2, { exact: true })}` : '',
      'This counts native title only. Roughly half the Territory is Aboriginal freehold under a different statute that these registers do not cover.',
    )}">
      <div class="stat-value">${nt ? esc(pct(nt.recognisedPct)) : '—'}</div>
      <div class="stat-label">Northern Territory, on this register</div>
      <div class="stat-note">Not a measure of Aboriginal land — see the note below</div>
    </div>
  `;

  /* ── legend: four teaching cards, in the fixed order, each opening the glossary ── */
  const legend = document.createElement('div');
  legend.className = 'jt-legend';
  legend.innerHTML = `
    <div class="card-sub">What the four categories mean — the first two are inside each square, the last two beside it.</div>
    <div class="legend-cards">
      ${TENURE_ORDER.map((t) => {
        const s = TENURE[t];
        const key = t === 'exclusive' ? 'exclusive-possession'
          : t === 'nonexclusive' ? 'non-exclusive'
          : t === 'notexist' ? 'does-not-exist' : 'extinguishment';
        return `<button type="button" class="legend-card" data-term="${esc(key)}"
            data-tip="${tipAttr(s.register, `${km2(nationalArea(meta, t), { exact: true })} nationally — dissolved union, land only`, 'Click for the full definition.')}">
          <span class="legend-swatch" style="${swatchStyle(t)}"></span>
          <span class="lc-body">
            <span class="lc-name">${esc(s.label)}</span>
            <span class="lc-num">${esc(km2(nationalArea(meta, t)))} nationally</span>
            <span class="lc-plain">${esc(s.plain)}</span>
          </span>
        </button>`;
      }).join('')}
    </div>
  `;

  /* ── the small multiples ── */
  const tiles = document.createElement('div');
  tiles.className = 'jt-tiles';
  const boxH = Math.max(...juris.map(sideOf));
  for (const j of juris) {
    tiles.append(buildTile(j, sideOf(j), trueSideOf(j), boxH));
    // The caveat sits immediately beside the square it corrects, at the weight
    // of the number it corrects. A reader who does not meet it here misreads
    // the whole view, so it is not a footnote and it is not hover-only.
    if (j.code === 'NT') tiles.append(alraCard(j));
  }

  /* ── the caveats that make the drawing readable ── */
  const notes = document.createElement('div');
  notes.className = 'jt-notes';
  notes.innerHTML = `
    <div class="note">
      <strong>The squares are area-true, with a declared floor.</strong>
      Each square’s area is proportional to the jurisdiction’s land area, so the side is the square root of it.
      At true scale the smallest squares would be a few pixels across and could be neither seen nor clicked, so no
      square is drawn smaller than ${FLOOR_PX}&nbsp;px.
      ${flooredNames.length
        ? `At this screen size that floor applies to ${esc(flooredNames.join(' and '))}, which are therefore drawn larger
           than their land area; each says so in its own caption and tooltip.`
        : 'At this screen size no square needed the floor.'}
      Every other square is at true scale.
    </div>
    <div class="note">
      <strong>The fill is a share of the jurisdiction, from the bottom up.</strong>
      ${glossTerm('exclusive-possession', 'Exclusive possession')} sits at the base as a solid block,
      ${glossTerm('non-exclusive', 'non-exclusive rights')} stippled above it. A high recognised share is not the same
      thing as a high exclusive share${sa ? `: South Australia is ${esc(pct(sa.recognisedPct))} recognised and
      ${esc(pct(shareOf(sa.exclusiveKm2, sa.landKm2)))} exclusive` : ''}.
      A category whose area is smaller than one pixel of height is drawn one pixel high so that it is present rather than
      invisible; its tooltip and the table below give the true figure.
    </div>
    <div class="note warn">
      <strong>The four categories do not partition a jurisdiction.</strong>
      That is why “${esc(TENURE.notexist.label.toLowerCase())}” and “${esc(TENURE.extinguished.label.toLowerCase())}” are drawn
      as a detached bar beside each square rather than stacked on top of the recognised block. They are separately
      measured unions of different ground, and most of every jurisdiction is in none of the four, because no
      ${glossTerm('determination', 'determination')} has ever considered it. The bar uses the same vertical scale as the
      square: full height is the whole jurisdiction.
      ${nsw ? `In New South Wales the ground where native title was found not to exist measures
        ${esc(km2(nsw.notExistKm2, { exact: true }))}, and the ground ${glossTerm('extinguishment', 'extinguished')}
        ${esc(km2(nsw.extinguishedKm2, { exact: true }))}, against ${esc(km2(nsw.recognisedKm2, { exact: true }))} recognised.
        Each of those is its own measured union: this page never adds two of them together.` : ''}
    </div>
    <div class="note legal">
      <strong>What the percentages are measured against.</strong>
      Every share on this page is against Geoscience Australia’s published land areas — ${esc(km2(meta.australiaLandKm2, { exact: true }))}
      for Australia — and <strong>excludes sea</strong>. Native title recognised over
      ${glossTerm('sea-determination', 'sea country')}, a further ${esc(km2(meta.areas.recognisedSeaKm2, { exact: true }))} nationally,
      is counted nowhere on this page. Every area is a ${glossTerm('dissolved-union', 'dissolved union')} measured in an
      ${glossTerm('equal-area-projection', 'equal-area projection')}, never a sum of the register’s per-record areas.
      The exclusive and non-exclusive blocks are measured as separate unions and together differ from the recognised
      union by less than 0.01% of a jurisdiction’s land area; each block is drawn from its own measured figure.
    </div>
  `;

  /* ── the same eight, as numbers ── */
  const totals = {
    land: meta.australiaLandKm2,
    recognised: meta.areas.recognisedKm2,
    recognisedPct: meta.areas.recognisedPct,
    exclusive: meta.areas.exclusiveKm2,
    exclusivePct: meta.areas.exclusivePct,
    determinations: juris.reduce((a, j) => a + j.determinations, 0),
    agreements: juris.reduce((a, j) => a + j.agreements, 0),
    live: juris.reduce((a, j) => a + j.liveMatters, 0),
  };
  const landSum = juris.reduce((a, j) => a + j.landKm2, 0);

  const table = document.createElement('div');
  table.className = 'stack-sm jt-table';
  table.innerHTML = `
    <h2 class="card-title">The same eight, as numbers</h2>
    <p class="small muted">In order of land area. Every row opens that jurisdiction on the map.
      Areas are given to the nearest square kilometre so they can be reconciled against the registers; the underlying
      boundaries are indicative and do not support that precision.</p>
    <div class="table-scroll">
      <table>
        <caption class="visually-hidden">Land area, recognised area, exclusive possession, determinations, agreements
          and live matters for each Australian jurisdiction.</caption>
        <thead>
          <tr>
            <th scope="col">Jurisdiction</th>
            <th scope="col" class="num">Land area km²</th>
            <th scope="col" class="num">Recognised km²</th>
            <th scope="col" class="num">% of land</th>
            <th scope="col" class="num">Exclusive km²</th>
            <th scope="col" class="num">Exclusive % of land</th>
            <th scope="col" class="num">Determinations</th>
            <th scope="col" class="num">Agreements</th>
            <th scope="col" class="num">Live matters</th>
          </tr>
        </thead>
        <tbody>
          ${juris.map((j) => {
            const exPct = shareOf(j.exclusiveKm2, j.landKm2);
            const nothing = j.determinations === 0;
            return `<tr class="clickable" data-juris="${esc(j.code)}" data-tip="${tipAttr(
              `${j.name} (${j.code})`,
              nothing ? 'No determination has ever been made here — a recorded zero.' : `${pct(j.recognisedPct)} of the ${noun(j.code).toLowerCase()} recognised, ${pct(exPct)} exclusive possession`,
              'Dissolved union, land only. Click to open this jurisdiction on the map.',
            )}">
              <th scope="row" class="jt-rowhead">
                <button type="button" class="jt-rowbtn">${esc(j.name)}</button>
                <span class="mono xsmall muted">${esc(j.code)}</span>
                ${nothing ? '<div class="xsmall muted">No determination has ever been made here</div>' : ''}
              </th>
              <td class="num">${esc(num(j.landKm2))}</td>
              <td class="num">${esc(num(j.recognisedKm2))}</td>
              <td class="num">${esc(pct(j.recognisedPct))}</td>
              <td class="num">${esc(num(j.exclusiveKm2))}</td>
              <td class="num">${esc(pct(exPct))}</td>
              <td class="num">${esc(num(j.determinations))}</td>
              <td class="num">${esc(num(j.agreements))}</td>
              <td class="num">${esc(num(j.liveMatters))}</td>
            </tr>`;
          }).join('')}
        </tbody>
        <tfoot>
          <tr data-tip="${tipAttr(
            'Australia — every jurisdiction together',
            `${km2(totals.recognised, { exact: true })} recognised of ${km2(totals.land, { exact: true })} of land`,
            'The jurisdictions do not overlap, so these rows sum exactly to the national union.',
          )}">
            <th scope="row" class="jt-rowhead">Australia <span class="xsmall muted">all jurisdictions</span></th>
            <td class="num">${esc(num(totals.land))}</td>
            <td class="num">${esc(num(totals.recognised))}</td>
            <td class="num">${esc(pct(totals.recognisedPct))}</td>
            <td class="num">${esc(num(totals.exclusive))}</td>
            <td class="num">${esc(pct(totals.exclusivePct))}</td>
            <td class="num">${esc(num(totals.determinations))}</td>
            <td class="num">${esc(num(totals.agreements))}</td>
            <td class="num">${esc(num(totals.live))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    <p class="xsmall muted">The Australia row uses Geoscience Australia’s national land area, which is
      ${esc(num(Math.round(meta.australiaLandKm2 - landSum)))} km² more than the eight rows sum to.
      Determinations, agreements and live matters sum exactly.</p>
  `;
  table.addEventListener('click', (e) => {
    const row = (e.target as Element)?.closest?.('tr[data-juris]');
    const code = row?.getAttribute('data-juris');
    if (code) navigate('map', { juris: code });
  });

  // The host itself is discarded by the router — only its CHILDREN are moved
  // into the view wrapper — so the layout container has to be one of them.
  const view = document.createElement('div');
  view.className = 'jt-view';
  view.append(head, stats, legend, tiles, notes, table);
  host.append(view);
}
